import React, { useEffect, useState } from 'react';
import { Box, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { MainDrawer } from '../Admin/CommonComponent/MainDrawer';
import { OKRButton } from '../Common/OKRButton';
import { BorderCheckboxIcon, CheckedIcon } from '../../config/svg/formElementIcons';
import { TargetQuarterOption, WhatsNewComingNextApiRow } from './mockData';
import { createComingNextAPI, updateComingNextAPI } from '../../services/whatsNewRoadmapService';

interface AddRoadmapItemDrawerProps {
	open: boolean;
	handleClose: () => void;
	onSaved: () => void;
	targetQuarters: TargetQuarterOption[];
	editEntity?: WhatsNewComingNextApiRow | null;
}

const emptyFormState = (defaultTargetQuarterId?: number) => ({
	title: '',
	description: '',
	targetQuarter: defaultTargetQuarterId,
	admins: false,
	users: true,
	status: 'draft' as 'draft' | 'published',
});

const formStateFromEntity = (entity: WhatsNewComingNextApiRow) => ({
	title: entity.title,
	description: entity.description,
	targetQuarter: entity.targetQuarter,
	admins: entity.isAdminImpacted,
	users: entity.isUserImpacted,
	status: (entity.isPublished ? 'published' : 'draft') as 'draft' | 'published',
});

export const AddRoadmapItemDrawer: React.FC<AddRoadmapItemDrawerProps> = ({
	open,
	handleClose,
	onSaved,
	targetQuarters,
	editEntity,
}) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [formData, setFormData] = useState(emptyFormState(targetQuarters[0]?.quarterId));
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!open) {
			return;
		}
		setFormData(editEntity ? formStateFromEntity(editEntity) : emptyFormState(targetQuarters[0]?.quarterId));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, editEntity]);

	const handleTextChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = event.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleCancel = () => {
		setFormData(emptyFormState(targetQuarters[0]?.quarterId));
		handleClose();
	};

	const isFormValid =
		Boolean(formData.title.trim()) &&
		Boolean(formData.description.trim()) &&
		formData.targetQuarter !== undefined &&
		(formData.admins || formData.users);

	const handleSaveClick = async () => {
		if (!isFormValid) {
			return;
		}
		const impactType = formData.admins && formData.users ? 3 : formData.admins ? 2 : 1;
		const entity = {
			...(editEntity ? { oneGuruComingNextId: editEntity.oneGuruComingNextId } : {}),
			title: formData.title,
			description: formData.description,
			targetQuarter: formData.targetQuarter,
			impactType,
			isAdminImpacted: formData.admins,
			isUserImpacted: formData.users,
			isPublished: formData.status === 'published',
		};
		try {
			setSaving(true);
			const response: any = editEntity ? await updateComingNextAPI(entity) : await createComingNextAPI(entity);
			if (response?.data?.isSuccess) {
				enqueueSnackbar(response.data.messageList?.Result, { variant: 'success' });
				setFormData(emptyFormState(targetQuarters[0]?.quarterId));
				onSaved();
				handleClose();
			} else {
				enqueueSnackbar(t('someErrorOccurred'), { variant: 'error' });
			}
		} catch (error) {
			enqueueSnackbar(t('someErrorOccurred'), { variant: 'error' });
		} finally {
			setSaving(false);
		}
	};

	return (
		<MainDrawer
			drawerClassName='main-drawer-panel add-update-drawer'
			open={open}
			headerTitle={editEntity ? t('editRoadmapItemTitle') : t('addRoadmapItemTitle')}
			showHeaderCloseIcon
			handleDrawerClose={handleCancel}
			handleCancelClick={handleCancel}
			handleSaveClick={handleSaveClick}
			cancelBtnText={t('cancelBtn')}
			saveButtonText={t('saveEntryBtn')}
			isSaveButtonDisabled={!isFormValid || saving}
			loader={saving}
		>
			<Box className='add-update-form'>
				<Box className='form-field'>
					<TextField
						fullWidth
						required
						id='roadmap-title'
						name='title'
						label={t('titleLabel')}
						placeholder={t('titleLabel')}
						value={formData.title}
						onChange={handleTextChange}
						InputLabelProps={{ shrink: true }}
					/>
				</Box>

				<Box className='form-field'>
					<TextField
						fullWidth
						required
						multiline
						minRows={4}
						id='roadmap-description'
						name='description'
						label={t('descriptionLabel')}
						placeholder={t('descriptionPlaceholder')}
						value={formData.description}
						onChange={handleTextChange}
						InputLabelProps={{ shrink: true }}
					/>
				</Box>

				<Box className='form-field'>
					<TextField
						select
						fullWidth
						required
						id='roadmap-target-quarter'
						name='targetQuarter'
						label={t('targetQuarterLabel')}
						value={formData.targetQuarter ?? ''}
						onChange={(event) => setFormData((prev) => ({ ...prev, targetQuarter: Number(event.target.value) }))}
						InputLabelProps={{ shrink: true }}
					>
						{targetQuarters.map((quarter) => (
							<MenuItem key={quarter.quarterId} value={quarter.quarterId}>
								{quarter.quarterName}
							</MenuItem>
						))}
					</TextField>
				</Box>

				<Box className='form-field'>
					<Typography variant='subtitle1' className='field-label'>
						{t('whosImpactedLabel')}
						<span className='asterisk'> *</span>
					</Typography>
					<Box className='impacted-audience-group'>
						<FormControlLabel
							className={`audience-chip ${formData.admins ? 'active' : ''}`}
							control={
								<Checkbox
									checked={formData.admins}
									icon={<BorderCheckboxIcon />}
									checkedIcon={<CheckedIcon />}
									onChange={(e) => setFormData((prev) => ({ ...prev, admins: e.target.checked }))}
								/>
							}
							label={t('adminsLabel')}
						/>
						<FormControlLabel
							className={`audience-chip ${formData.users ? 'active' : ''}`}
							control={
								<Checkbox
									checked={formData.users}
									icon={<BorderCheckboxIcon />}
									checkedIcon={<CheckedIcon />}
									onChange={(e) => setFormData((prev) => ({ ...prev, users: e.target.checked }))}
								/>
							}
							label={t('usersLabel')}
						/>
					</Box>
					<Typography className='field-helper-text'>{t('impactedAudienceHelperText')}</Typography>
				</Box>

				<Box className='form-field'>
					<Typography variant='subtitle1' className='field-label'>
						{t('statusLabel')}
						<span className='asterisk'> *</span>
					</Typography>
					<Box className='status-toggle-group'>	
						<OKRButton
							id='roadmap-status-draft'
							className={`status-toggle-btn ${formData.status === 'draft' ? 'active' : ''}`}
							text={t('draftLabel')}
							handleClick={() => setFormData((prev) => ({ ...prev, status: 'draft' }))}
						/>
						<OKRButton
							id='roadmap-status-published'
							className={`status-toggle-btn ${formData.status === 'published' ? 'active' : ''}`}
							text={t('publishedLabel')}
							handleClick={() => setFormData((prev) => ({ ...prev, status: 'published' }))}
						/>
					</Box>
				</Box>
			</Box>
		</MainDrawer>
	);
};
